import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { MONITORING_WINDOWS, type MonitoringWindow } from "./types";

export const MONITORING_TABS = ["overview", "pods", "cameras", "pipeline", "bb", "requests", "events", "backend"] as const;
export type MonitoringTab = (typeof MONITORING_TABS)[number];

/** Which backend instances the metrics / events come from: one instance, all roles, or (default) own role. */
export interface MonitoringScope {
  instance: string | null;
  all: boolean;
}

interface MonitoringContextValue {
  tab: MonitoringTab;
  setTab: (tab: MonitoringTab) => void;
  window: MonitoringWindow;
  setWindow: (window: MonitoringWindow) => void;
  scope: MonitoringScope;
  setScope: (scope: MonitoringScope) => void;
  /** auto-refresh off: every board query stops polling */
  paused: boolean;
  setPaused: (paused: boolean) => void;
  refreshAll: () => void;
}

const MonitoringContext = createContext<MonitoringContextValue | null>(null);

const isWindow = (value: string | null): value is MonitoringWindow =>
  !!value && (MONITORING_WINDOWS as string[]).includes(value);
const isTab = (value: string | null): value is MonitoringTab =>
  !!value && (MONITORING_TABS as readonly string[]).includes(value);

/** Board state lives in the URL (tab, window, instance) so a view can be shared and survives a reload. */
export function MonitoringProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [paused, setPaused] = useLocalStorageState("pizza-admin:monitoring-paused:v1", false);

  const tabParam = params.get("tab");
  const windowParam = params.get("window");
  const tab: MonitoringTab = isTab(tabParam) ? tabParam : "overview";
  const window: MonitoringWindow = isWindow(windowParam) ? windowParam : "6h";
  const instanceParam = params.get("instance");
  const allParam = params.get("scope") === "all";

  const update = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const value = useMemo<MonitoringContextValue>(
    () => ({
      tab,
      setTab: (next) => update({ tab: next === "overview" ? null : next }),
      window,
      setWindow: (next) => update({ window: next === "6h" ? null : next }),
      scope: { instance: instanceParam, all: !instanceParam && allParam },
      setScope: (next) =>
        update({ instance: next.instance, scope: !next.instance && next.all ? "all" : null }),
      paused,
      setPaused,
      refreshAll: () => {
        void queryClient.invalidateQueries({ queryKey: ["monitoring"] });
      },
    }),
    [tab, window, instanceParam, allParam, paused, setPaused, update, queryClient],
  );

  return <MonitoringContext.Provider value={value}>{children}</MonitoringContext.Provider>;
}

export function useMonitoring() {
  const context = useContext(MonitoringContext);
  if (!context) throw new Error("useMonitoring must be used inside <MonitoringProvider>");
  return context;
}
